import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { SplashScreen } from "./components/SplashScreen";
import { OnboardingTour } from "./components/OnboardingTour";
import { ScrollToTop } from "./components/ScrollToTop";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AppLayout } from "./components/Layout/AppLayout";
import { setupGlobalErrorHandlers } from "./lib/errorLogger";
import logo from "@/assets/logo.png";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Inbox from "./pages/Inbox";
import Autopilot from "./pages/Autopilot";
import Runway from "./pages/Runway";
import Exceptions from "./pages/Exceptions";
import Sentinel from "./pages/Sentinel";
import Sync from "./pages/Sync";
import Watch from "./pages/Watch";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";
import Onboarding from "./pages/Onboarding";
import Pricing from "./pages/Pricing";
import Features from "./pages/Features";
import About from "./pages/About";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Support from "./pages/Support";
import Documentation from "./pages/Documentation";
import APIReference from "./pages/APIReference";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import NotFound from "./pages/NotFound";

// Setup global error handlers on app load
setupGlobalErrorHandlers();

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <img 
            src={logo} 
            alt="SENTINEL" 
            className="w-16 h-16 mx-auto brightness-0 dark:brightness-100 dark:invert animate-pulse"
          />
          <p className="text-muted-foreground font-medium">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return (
    <AppLayout>
      {children}
    </AppLayout>
  );
}

const App = () => {
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    // Register service worker for offline support and push notifications
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration.scope);
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <TooltipProvider>
          <ErrorBoundary>
            <AuthProvider>
              {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ScrollToTop />
                <OnboardingTour />
                <PWAInstallPrompt />
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/features" element={<Features />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/documentation" element={<Documentation />} />
                  <Route path="/api-reference" element={<APIReference />} />
                  <Route path="/support" element={<Support />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                  <Route path="/dashboard" element={<Navigate to="/inbox" replace />} />
                  <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
                  <Route path="/autopilot" element={<Navigate to="/settings" replace />} />
                  <Route path="/sentinel" element={<ProtectedRoute><Sentinel /></ProtectedRoute>} />
                  <Route path="/runway" element={<ProtectedRoute><Runway /></ProtectedRoute>} />
                  <Route path="/exceptions" element={<ProtectedRoute><Exceptions /></ProtectedRoute>} />
                  <Route path="/sync" element={<ProtectedRoute><Sync /></ProtectedRoute>} />
                  <Route path="/watch" element={<ProtectedRoute><Watch /></ProtectedRoute>} />
                  <Route path="/foresight" element={<Navigate to="/runway" replace />} />
                  <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
                  <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                  <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
            </AuthProvider>
          </ErrorBoundary>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
